import { Caption } from "@bounce/design-system"
import { useFormatDecimals, useFormatIsoDate, useFormatRelativeDate, useLocale, useTranslation } from "@bounce/i18n"
import { getReviewFullName, useReviewUsername } from "@bounce/reviews"
import { RatingStars, Separator, Truncate } from "@bounce/web-components"

import { StoreCardTag } from "./StoreCardTag"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"

import HeartIcon from "@bounce/assets/icons/Heart.svg"

type StoreCardReviewProps = {
  rating: number
  review: NearbyStorageSpot["reviews"]["edges"][0]["node"]
}

export const StoreCardReview = ({ rating, review }: StoreCardReviewProps) => {
  const { t } = useTranslation()
  const { language } = useLocale()

  const formatDecimals = useFormatDecimals({ minDecimalPlaces: 1, maxDecimalPlaces: 2 })
  const formatRelative = useFormatRelativeDate()
  const formatIsoDate = useFormatIsoDate()
  const username = useReviewUsername({
    review: {
      user: {
        fullName: getReviewFullName(review.user),
      },
    },
  })

  if (!review.rating) {
    return null
  }

  return (
    <div className="-mt-4 min-h-[86px] rounded-lg border border-neutral-200 bg-neutral-100 pt-4">
      <div className="flex flex-row p-5">
        <div className="flex flex-col items-center justify-center">
          <p className="title4">{formatDecimals(rating)}</p>
          <RatingStars className="mt-px gap-x-0" ratingScore={rating} starClassName="text-black size-4" />
          {rating >= 4.85 && (
            <StoreCardTag
              bgColor="purple"
              className="mx-auto mt-2"
              icon={<HeartIcon className="text-lilac-700" />}
              text={t("cmp.storeCard.topRatedTagLabel", "Top rated")}
            />
          )}
        </div>
        <Separator direction="vertical" className="mx-6" />
        <div className="flex flex-col">
          <RatingStars className="mt-px gap-x-0" ratingScore={review.rating} starClassName="text-black size-4" />
          <Truncate as="p" className="mt-2 text-sm" numberOfLines={4}>
            {review.translatedFeedback ?? review.feedback}
          </Truncate>
          <span className="mt-2 text-xs font-semibold text-neutral-900">{username}</span>
          {!!review.completedAt && (
            <p className="text-xs text-neutral-800">
              <time dateTime={formatIsoDate(review.completedAt)}>{formatRelative(review.completedAt)}</time>
            </p>
          )}
          {review.language !== language && !!review.translatedFeedback && (
            <Caption className="mt-2 text-neutral-800">
              {t("cmp.storeCard.automaticallyTranslatedLabel", "Automatically translated")}
            </Caption>
          )}
        </div>
      </div>
    </div>
  )
}
